import type { OrgEventChecklistItemInstance } from './orgEventHooks';

/** Event Response Checklist / Courses of Action: items with no resource scope. */
export function eventLevelChecklistItems(
  items: OrgEventChecklistItemInstance[] | null | undefined
): OrgEventChecklistItemInstance[] {
  return (items ?? []).filter((item) => !item.resourceSetId);
}

/** Asset expand: items stamped with this resource set. */
export function resourceChecklistItems(
  items: OrgEventChecklistItemInstance[] | null | undefined,
  resourceSetId: string | null | undefined
): OrgEventChecklistItemInstance[] {
  const id = resourceSetId?.trim();
  if (!id) return [];
  return (items ?? []).filter((item) => item.resourceSetId === id);
}

export function hasPersistedResourceChecklistItems(
  items: OrgEventChecklistItemInstance[] | null | undefined
): boolean {
  return (items ?? []).some((item) => !!item.resourceSetId);
}

export type ResponseActionProgress = {
  complete: number;
  total: number;
};

/**
 * Event-card count. Live org-action statuses win once that list has loaded, so
 * closing a task moves the count without waiting on the event payload. Until
 * then, and when the live list cannot see the checklist, keep the server
 * snapshot.
 */
export function eventResponseActionProgress(params: {
  items: OrgEventChecklistItemInstance[] | null | undefined;
  actionsLoaded: boolean;
  actions: Array<{
    actionId: string;
    status?: string | null;
    archived?: boolean;
  }>;
  server?: ResponseActionProgress | null;
}): ResponseActionProgress | null {
  const server =
    params.server && params.server.total > 0 ? params.server : null;
  if (!params.actionsLoaded) return server;

  const statusByActionId = new Map<string, string | null | undefined>();
  for (const action of params.actions) {
    if (action.archived) continue;
    statusByActionId.set(String(action.actionId), action.status);
  }
  const live = assetCoaProgressFromItems(
    eventLevelChecklistItems(params.items),
    statusByActionId
  );
  if (live.total > 0) {
    return { complete: live.complete, total: live.total };
  }
  return server;
}

export function assetCoaProgressFromItems(
  items: OrgEventChecklistItemInstance[],
  statusByActionId: Map<string, string | null | undefined>
): { complete: number; total: number; percent: number } {
  let total = 0;
  let complete = 0;

  for (const item of items) {
    if (item.kind === 'send_notification') {
      total += 1;
      if (item.sentNotificationId) complete += 1;
      continue;
    }
    const status = statusByActionId.get(item.actionId);
    if (status == null) continue;
    total += 1;
    if (status === 'Closed') complete += 1;
  }

  const percent = total === 0 ? 0 : Math.round((complete / total) * 100);
  return { complete, total, percent };
}

/**
 * Persisted empty playbook: at-risk only. DHS fallback only when nothing was
 * persisted.
 */
export function shouldShowAssetCoursesOfAction(params: {
  usePersistedChecklists: boolean;
  persistedItemCount: number;
  dhsFallback?: boolean;
}): boolean {
  if (params.usePersistedChecklists) return params.persistedItemCount > 0;
  return !!params.dhsFallback;
}
